import { useLanguage } from "@/contexts/LanguageContext";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Star, Users, Car, Shield, Award, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

export default function AboutPage() {
  const { t } = useLanguage();

  return (
    <Layout>
      <SEO title="About Us" description="Euro Cars - Your trusted car rental partner in Thessaloniki since 2020. 30+ vehicles, competitive rates, airport delivery." />
      {/* Hero */}
      <section className="relative py-20 md:py-32 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1770847816156-a4041d979580?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 data-testid="about-title" className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4">
            {t("about.title")}
          </h1>
          <p className="font-dm-sans text-base md:text-lg text-white/70 max-w-2xl">
            {t("about.subtitle")}
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-dm-sans text-base md:text-lg text-slate-600 leading-relaxed mb-8">
                {t("about.story")}
              </p>
              <h3 className="font-outfit font-semibold text-2xl text-slate-900 mb-4">{t("about.mission")}</h3>
              <p className="font-dm-sans text-base text-slate-500 leading-relaxed">
                {t("about.missionText")}
              </p>
            </div>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3]">
              <img
                src="https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?auto=format&fit=crop&q=80&w=800"
                alt="Our fleet"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Car, value: "30+", label: "Vehicles" },
              { icon: Users, value: "1000+", label: "Happy Customers" },
              { icon: Award, value: "5+", label: "Years Experience" },
              { icon: Clock, value: "24/7", label: "Support" },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-orange-500/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <stat.icon className="w-7 h-7 text-orange-500" />
                </div>
                <p className="font-outfit font-bold text-3xl text-slate-900">{stat.value}</p>
                <p className="font-dm-sans text-sm text-slate-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-outfit font-semibold text-3xl md:text-5xl text-slate-900 tracking-tight mb-10 text-center">
            {t("about.whyUs")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {t("about.reasons").map((reason, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl hover:bg-slate-50 transition-colors">
                <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center mt-0.5 shrink-0">
                  <Star className="w-3 h-3 text-white" />
                </div>
                <span className="font-dm-sans text-sm text-slate-600">{reason}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/fleet">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white font-outfit font-bold rounded-full px-10 py-6 text-lg shadow-xl hover:shadow-orange-500/30 transition-all hover:-translate-y-1">
                {t("hero.cta")}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
